import type { TrainingProfile } from '../schema/profile';

const t = (cs: string, en: string) => ({ 'cs-CZ': cs, 'en-US': en });

/**
 * Žebříky a lešení - V1 DRAFT - authored for structural completeness; NOT a
 * substitute for sign-off by a Czech osoba odborně způsobilá v prevenci rizik.
 * Legal refs: NV 362/2005 Sb. příloha čl. III (žebříky) a čl. VII (dočasné
 * stavební konstrukce - lešení). Complements the work-at-heights profile
 * (which owns fall-arrest > 1,5 m); this one covers the everyday sub-cases.
 * Bilingual (cs-CZ + en-US). Three chapters (žebřík | lešení | počasí a
 * kontrola) + a graded quiz with one question per kind. Answer keys live here
 * (repo-privileged) and in the DB; stripped from the client payload by the API.
 */
export const laddersScaffoldingProfile: TrainingProfile = {
  slug: 'ladders-scaffolding',
  // Top-level title is a PLAIN STRING - seeded verbatim into the varchar column.
  title: 'Žebříky a lešení',
  passThreshold: 80,
  content: {
    schemaVersion: 1,
    chapters: [
      {
        id: 'ch-zebrik',
        title: t('Bezpečně na žebříku', 'Safe on a ladder'),
        minDwellSeconds: 20,
        blocks: [
          {
            id: 'h-zebrik',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Tři body opory', 'Three points of contact'),
          },
          {
            id: 'p-zebrik-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Na žebříku měj vždy **tři body opory** (dvě nohy + ruka). Z žebříku se dělají jen **krátkodobé, nenáročné práce** - ne dlouhé montáže oběma rukama. **Nevykláněj se** (pupík mezi štěřinami), nářadí nos v brašně u pasu, a na štaflích nikdy nestůj na **horních dvou příčkách**.',
              "Keep **three points of contact** on a ladder (two feet + one hand). Ladders are for **short, light tasks only** - not long two-handed installations. **Don't lean out** (belt buckle between the stiles), carry tools in a belt pouch, and never stand on the **top two rungs** of a stepladder.",
            ),
          },
          {
            id: 'anim-zebrik-kontakt',
            blockType: 'animation',
            schemaVersion: 1,
            scene: 'ladderContact',
            steps: [
              {
                label: t('Tři body opory', 'Three points of contact'),
                say: t(
                  'Dvě nohy a ruka, nebo dvě ruce a noha.',
                  'Two feet and a hand, or two hands and a foot.',
                ),
              },
              {
                label: t('Obě ruce plné', 'Both hands full'),
                say: t(
                  'Vyklánění s plnýma rukama končí pádem.',
                  'Leaning out with full hands ends in a fall.',
                ),
              },
            ],
            caption: t(
              'Tři body opory drží. Obě ruce plné + vyklánění = pád.',
              'Three points of contact hold you. Both hands full + leaning out = a fall.',
            ),
          },
          {
            id: 'c-zebrik-pravo',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'info',
            markdown: t(
              'Právní základ: **NV 362/2005 Sb., příloha čl. III** - mj. sklon opěrného žebříku ~**4 : 1** a **přesah nejméně 1,1 m** nad výstupní plochu (viz i kurz Práce ve výškách).',
              'Legal basis: **Gov. Reg. 362/2005 Coll., annex art. III** - incl. the ~**4:1** lean angle and **at least 1.1 m of overhang** above the landing (see also the Work at Heights course).',
            ),
          },
        ],
      },
      {
        id: 'ch-leseni',
        title: t('Lešení: štítek a zábradlí', 'Scaffolding: the tag and the railing'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['kc-stitek'],
        blocks: [
          {
            id: 'h-leseni',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t(
              'Na cizí lešení jen s předávacím protokolem',
              'Only use scaffolding that was formally handed over',
            ),
          },
          {
            id: 'p-leseni-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Lešení smí být užíváno až po **předání do užívání** (protokol / štítek na lešení). Kompletní musí být **zábradlí (madlo ve výšce 1,1 m)**, **zarážka u podlahy** a plné podlahy. **Nikdy neodstraňuj** zábradlí ani neupravuj konstrukci - to smí jen lešenáři, kteří ji postavili.',
              'Scaffolding may only be used after **formal handover** (protocol / tag on the scaffold). It must have a complete **railing (top rail at 1.1 m)**, a **toe board** and full decking. **Never remove** railings or alter the structure - only the erectors may.',
            ),
          },
          {
            id: 'kc-stitek',
            blockType: 'knowledgeCheck',
            schemaVersion: 1,
            prompt: t(
              'Na lešení chybí štítek o předání a jedno pole zábradlí. Co uděláš?',
              'The scaffold has no handover tag and one railing bay is missing. What do you do?',
            ),
            choices: [
              { id: 'kc-a', label: t('Nepoužiju ho a ohlásím to', "Don't use it and report it") },
              {
                id: 'kc-b',
                label: t(
                  'Vylezu opatrně jen na první patro',
                  'Climb carefully, just to the first level',
                ),
              },
              {
                id: 'kc-c',
                label: t('Doplním zábradlí sám prknem', 'Fit a plank as a railing myself'),
              },
            ],
            correctChoiceId: 'kc-a',
            explanation: t(
              'Bez předání a s neúplným zábradlím je lešení mimo provoz. Úpravy smí dělat jen ten, kdo lešení staví.',
              'Without handover and with incomplete railings the scaffold is out of service. Only the erectors may modify it.',
            ),
          },
        ],
      },
      {
        id: 'ch-pocasi',
        title: t('Počasí a průběžná kontrola', 'Weather and ongoing checks'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['ord-kontrola'],
        blocks: [
          {
            id: 'h-pocasi',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Vítr, náledí, přetížení', 'Wind, ice, overload'),
          },
          {
            id: 'p-pocasi-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Práci na žebříku i lešení přeruš při **silném větru, bouřce, náledí nebo snížené viditelnosti**. Podlahy lešení **nepřetěžuj** materiálem a udržuj je čisté - námraza a malta na podlážce jsou skluzavka.',
              "Stop ladder and scaffold work in **strong wind, storms, ice or poor visibility**. Don't **overload** scaffold decks with material and keep them clean - frost or mortar on a deck is a slide.",
            ),
          },
          {
            id: 'ord-kontrola',
            blockType: 'ordering',
            schemaVersion: 1,
            prompt: t(
              'Seřaď rychlou kontrolu žebříku před použitím.',
              'Order the quick pre-use ladder check.',
            ),
            items: [
              {
                id: 'ok-1',
                label: t(
                  'Stav štěřin a příček (praskliny, ohnutí)',
                  'Stiles and rungs (cracks, bends)',
                ),
              },
              {
                id: 'ok-2',
                label: t(
                  'Protiskluzové patky a stabilní podklad',
                  'Anti-slip feet and stable ground',
                ),
              },
              {
                id: 'ok-3',
                label: t('Správný sklon a přesah 1,1 m', 'Correct lean and 1.1 m overhang'),
              },
              {
                id: 'ok-4',
                label: t('Výstup se třemi body opory', 'Climb with three points of contact'),
              },
            ],
          },
          {
            id: 'k-pocasi',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              '**Žebřík = krátce a lehce; lešení = jen předané a kompletní.** Při zhoršeném počasí práci ve výšce přeruš.',
              '**Ladders = short and light; scaffolds = only handed-over and complete.** Pause height work in bad weather.',
            ),
          },
        ],
      },
    ],
  },
  questions: [
    {
      id: 'q-staffle-single',
      kind: 'single',
      chapterId: 'ch-zebrik',
      prompt: t('Kde na štaflích nesmíš stát?', 'Where must you not stand on a stepladder?'),
      choices: [
        { id: 'a', label: t('Na horních dvou příčkách', 'On the top two rungs') },
        { id: 'b', label: t('Ve spodní třetině', 'In the bottom third') },
        { id: 'c', label: t('Uprostřed', 'In the middle') },
      ],
      correct: 'a',
    },
    {
      id: 'q-leseni-tf',
      kind: 'truefalse',
      chapterId: 'ch-leseni',
      prompt: t(
        'Překážející pole zábradlí lešení si smím dočasně odmontovat, když ho pak vrátím.',
        'I may temporarily remove a scaffold railing bay that is in my way, as long as I put it back.',
      ),
      correct: false,
    },
    {
      id: 'q-zebrik-multi',
      kind: 'multi',
      chapterId: 'ch-zebrik',
      prompt: t(
        'Co platí pro práci ze žebříku? (vyber všechny správné)',
        'What applies to working from a ladder? (select all that apply)',
      ),
      choices: [
        { id: 'a', label: t('Jen krátkodobé a nenáročné práce', 'Short, light tasks only') },
        { id: 'b', label: t('Tři body opory', 'Three points of contact') },
        {
          id: 'c',
          label: t('Vyklánění je v pořádku do půl metru', 'Leaning out up to half a metre is fine'),
        },
        { id: 'd', label: t('Nářadí v brašně u pasu', 'Tools in a belt pouch') },
      ],
      correct: ['a', 'b', 'd'],
    },
    {
      id: 'q-madlo-numeric',
      kind: 'numeric',
      chapterId: 'ch-leseni',
      prompt: t(
        'V jaké výšce (v metrech) musí být horní madlo zábradlí lešení?',
        "At what height (in metres) must the scaffold's top rail be?",
      ),
      correct: 1.1,
      tolerance: 0,
    },
    {
      id: 'q-kontrola-order',
      kind: 'ordering',
      chapterId: 'ch-pocasi',
      prompt: t('Seřaď kontrolu žebříku.', 'Order the ladder check.'),
      choices: [
        { id: 'a', label: t('Stav štěřin a příček', 'Stiles and rungs') },
        { id: 'b', label: t('Patky a podklad', 'Feet and ground') },
        { id: 'c', label: t('Sklon a přesah', 'Lean and overhang') },
        { id: 'd', label: t('Výstup se třemi body opory', 'Climb with three points') },
      ],
      correct: ['a', 'b', 'c', 'd'],
    },
  ],
};
